import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useTabSleepDetector — notices when the browser stopped this page while it was in
 * the background, so the Active Alerts pages can say so and sound one catch-up tone.
 *
 * Browsers save memory by pausing background tabs: Edge "sleeping tabs", Chrome
 * "Memory Saver", and the energy saver modes in both. A paused page runs no code, so
 * no alerts arrive and no sound can play until the user comes back. Three signals,
 * because no single one is reliable everywhere:
 *
 *  - `document.wasDiscarded`: the browser threw the page away and reloaded it when
 *    the user returned (Chromium). Checked once, on mount.
 *  - the `resume` event: the page was frozen and is running again (Chromium Page
 *    Lifecycle API; Edge sleeping tabs freeze rather than discard).
 *  - a heartbeat gap: a 15s interval that comes back minutes late means the page
 *    was stopped. This also catches Firefox and Safari, and the computer itself
 *    sleeping. Chromium throttles timers in hidden tabs to about once a minute, so
 *    the threshold sits well above that to avoid flagging ordinary throttling.
 *
 * Returns:
 *  - `wake`: null, or { reason: 'discarded' | 'frozen' | 'paused', at, id }. `id`
 *    increases on every new wake, so effects can key on it.
 *  - `dismiss()`: clears `wake` (the banner's close button).
 *  - `allowSound()`: the catch-up gate. Outside a catch-up window it always allows.
 *    For a short window after a wake it allows exactly ONE play: alerts that piled
 *    up while asleep arrive together (a reload, or a socket backlog), and they get a
 *    single tone, not one per alert.
 */

const HEARTBEAT_MS = 15000;
const PAUSE_GAP_MS = 3 * 60 * 1000;
// Two signals for one sleep (resume + the late heartbeat) are one wake.
const COALESCE_MS = 10000;
// How long after waking the backlog is still arriving: covers the provider's 2s
// reconnect delay plus the alert reload.
const CATCH_UP_WINDOW_MS = 30000;
// Once the catch-up tone has played, stragglers from the same backlog get this long
// to land silently before live alerts sound normally again.
const CATCH_UP_SETTLE_MS = 5000;

const wasDiscardedOnLoad = () => typeof document !== 'undefined' && !!document.wasDiscarded;

export default function useTabSleepDetector() {
  const [wake, setWake] = useState(() =>
    wasDiscardedOnLoad() ? { reason: 'discarded', at: Date.now(), id: 1 } : null);
  const lastWakeAtRef = useRef(wake ? wake.at : 0);
  const lastTickRef = useRef(Date.now());
  const catchUpRef = useRef(
    wake ? { until: wake.at + CATCH_UP_WINDOW_MS, played: false } : { until: 0, played: false });

  const report = useCallback((reason) => {
    const now = Date.now();
    if (now - lastWakeAtRef.current < COALESCE_MS) return;
    lastWakeAtRef.current = now;
    catchUpRef.current = { until: now + CATCH_UP_WINDOW_MS, played: false };
    setWake((prev) => ({ reason, at: now, id: (prev?.id || 0) + 1 }));
  }, []);

  useEffect(() => {
    const onResume = () => report('frozen');
    document.addEventListener('resume', onResume);

    lastTickRef.current = Date.now();
    const timer = setInterval(() => {
      const now = Date.now();
      const gap = now - lastTickRef.current;
      lastTickRef.current = now;
      if (gap > PAUSE_GAP_MS) report('paused');
    }, HEARTBEAT_MS);

    return () => {
      document.removeEventListener('resume', onResume);
      clearInterval(timer);
    };
  }, [report]);

  const dismiss = useCallback(() => setWake(null), []);

  const allowSound = useCallback(() => {
    const c = catchUpRef.current;
    const now = Date.now();
    if (now >= c.until) return true;
    if (c.played) return false;
    c.played = true;
    c.until = Math.min(c.until, now + CATCH_UP_SETTLE_MS);
    return true;
  }, []);

  return { wake, dismiss, allowSound, wasDiscarded: wasDiscardedOnLoad() };
}
